import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTeam } from '@/hooks/useTeam';
import { usePipelines } from '@/hooks/usePipelines';
import { useLossReasons } from '@/hooks/useLossReasons';
import { useCurrency } from '@/hooks/useCurrency';
import { useAuth } from '@/contexts/AuthContext';
import { Loader as Loader2, Plus } from 'lucide-react';
import type { ContactOpportunity } from '@/hooks/useContactDetails';
import { useTranslation } from "react-i18next";
import {
  dateInputToIso,
  isFutureDateInputValue,
  isoToDateInputValue,
  todayDateInputValue,
} from '@/lib/dateInput';
import { deriveOpportunityStatus } from '@/lib/opportunityOutcome';

export interface OpportunityEditData {
  title: string;
  pipeline_id: string;
  stage_id: string;
  product?: string;
  price?: number;
  status: 'open' | 'won' | 'lost';
  closed_at?: string;
  sale_date?: string;
  won_by_user_id?: string;
  loss_reason_id?: string;
  lost_by_user_id?: string;
  notes?: string;
}

interface EditOpportunityFullDialogProps {
  open: boolean;
  onClose: () => void;
  /** `false` mantém o diálogo aberto: a gravação falhou e o que foi digitado não pode sumir. */
  onSave: (data: OpportunityEditData) => Promise<boolean | void>;
  opportunity?: ContactOpportunity | null;
}

export function EditOpportunityFullDialog({
  open,
  onClose,
  onSave,
  opportunity,
}: EditOpportunityFullDialogProps) {
  const { t } = useTranslation("crm");
  const { members, loading: loadingMembers } = useTeam();
  const { pipelines, pipelineStages } = usePipelines();
  const { lossReasons, loading: loadingReasons, createLossReason } = useLossReasons();
  const { symbol } = useCurrency();
  const { user } = useAuth();

  const [title, setTitle] = useState('');
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [product, setProduct] = useState('');
  const [price, setPrice] = useState('');
  const [saleDate, setSaleDate] = useState('');
  const [wonByUserId, setWonByUserId] = useState('');
  const [lossReasonId, setLossReasonId] = useState('');
  const [lostByUserId, setLostByUserId] = useState('');
  const [isAddingReason, setIsAddingReason] = useState(false);
  const [newReasonName, setNewReasonName] = useState('');
  const [isCreatingReason, setIsCreatingReason] = useState(false);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const activeMembers = members.filter((m) => m.is_active);
  const currentMemberId =
    activeMembers.find((member) => member.auth_user_id === user?.auth_user_id)?.id ?? '';

  const derivedStatus = deriveOpportunityStatus(
    stageId ? pipelineStages.find(s => s.id === stageId)?.stage_type : undefined
  );

  useEffect(() => {
    if (open && opportunity) {
      setTitle(opportunity.title || '');
      setPipelineId(opportunity.pipeline_id || '');
      setStageId(opportunity.pipeline_stage_id || '');
      setProduct(opportunity.product || '');
      setPrice(opportunity.price ? String(opportunity.price) : '');
      setSaleDate(isoToDateInputValue(opportunity.sale_date));
      setWonByUserId(opportunity.won_by_user_id || '');
      setLossReasonId(opportunity.loss_reason_id || '');
      setLostByUserId(opportunity.lost_by_user_id || '');
      setIsAddingReason(false);
      setNewReasonName('');
      setNotes(opportunity.notes || '');
    }
    // Só o id: quem renderiza monta um objeto novo a cada render, e depender dele
    // reescreveria os campos com os valores gravados enquanto o usuário digita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, opportunity?.id]);

  const filteredStages = pipelineId
    ? pipelineStages.filter((s) => s.pipeline_id === pipelineId)
    : [];

  const today = todayDateInputValue();
  const saleDateIsFuture =
    derivedStatus === 'won' && isFutureDateInputValue(saleDate);
  // O motivo é o dado que dá sentido à perda, então ele é exigido aqui em vez de
  // ser pedido depois num segundo diálogo
  const missingLossReason = derivedStatus === 'lost' && !lossReasonId;

  const handlePipelineChange = (newPipelineId: string) => {
    setPipelineId(newPipelineId);
    const stages = pipelineStages.filter((s) => s.pipeline_id === newPipelineId);
    if (stages.length > 0) {
      applyStage(stages[0].id);
    } else {
      setStageId('');
    }
  };

  // Ao trocar de etapa os campos do desfecho anterior são limpos e os do novo
  // desfecho já vêm preenchidos com os padrões (hoje / usuário logado)
  const applyStage = (newStageId: string) => {
    setStageId(newStageId);
    const stage = pipelineStages.find(s => s.id === newStageId);
    const stageType = stage?.stage_type ?? 'normal';

    if (stageType === 'won') {
      setLossReasonId('');
      setLostByUserId('');
      setIsAddingReason(false);
      setSaleDate((current) => current || todayDateInputValue());
      setWonByUserId((current) => current || currentMemberId);
    } else if (stageType === 'lost') {
      setSaleDate('');
      setWonByUserId('');
      setLostByUserId((current) => current || currentMemberId);
    } else {
      setSaleDate('');
      setWonByUserId('');
      setLossReasonId('');
      setLostByUserId('');
      setIsAddingReason(false);
    }
  };

  const handleReasonChange = (value: string) => {
    if (value === '__new__') {
      setIsAddingReason(true);
      setLossReasonId('');
    } else {
      setLossReasonId(value);
      setIsAddingReason(false);
    }
  };

  const handleCreateNewReason = async () => {
    if (!newReasonName.trim()) return;

    setIsCreatingReason(true);
    const newReason = await createLossReason(newReasonName.trim());
    setIsCreatingReason(false);

    if (newReason) {
      setLossReasonId(newReason.id);
      setNewReasonName('');
      setIsAddingReason(false);
    }
  };

  const canSave =
    Boolean(title.trim()) &&
    Boolean(pipelineId) &&
    Boolean(stageId) &&
    !saleDateIsFuture &&
    !missingLossReason;

  // Ganha e perdida fecham o card; a data de fechamento deixou de ser um campo do
  // formulário porque para venda ela é a própria data da venda e para perda não há
  // nada a informar além do momento em que o card foi fechado
  const resolveClosedAt = (status: 'open' | 'won' | 'lost'): string | undefined => {
    if (status === 'open') return undefined;
    if (status === 'won') {
      return dateInputToIso(saleDate) || opportunity?.closed_at || new Date().toISOString();
    }
    return opportunity?.closed_at || new Date().toISOString();
  };

  const handleSave = async () => {
    if (!canSave) return;

    setIsSubmitting(true);

    const data: OpportunityEditData = {
      title: title.trim(),
      pipeline_id: pipelineId,
      stage_id: stageId,
      product: product.trim() || undefined,
      price: price ? parseFloat(price) : undefined,
      status: derivedStatus,
      closed_at: resolveClosedAt(derivedStatus),
      sale_date: derivedStatus === 'won' ? dateInputToIso(saleDate) : undefined,
      won_by_user_id: derivedStatus === 'won' && wonByUserId ? wonByUserId : undefined,
      loss_reason_id: derivedStatus === 'lost' && lossReasonId ? lossReasonId : undefined,
      lost_by_user_id: derivedStatus === 'lost' && lostByUserId ? lostByUserId : undefined,
      notes: notes.trim() || undefined,
    };

    try {
      const saved = await onSave(data);
      if (saved === false) return;
      handleClose();
    } catch (error) {
      console.error('Error saving opportunity:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setPipelineId('');
    setStageId('');
    setProduct('');
    setPrice('');
    setSaleDate('');
    setWonByUserId('');
    setLossReasonId('');
    setLostByUserId('');
    setIsAddingReason(false);
    setNewReasonName('');
    setNotes('');
    onClose();
  };

  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    if (!numericValue) return '';
    const number = parseInt(numericValue, 10) / 100;
    return number.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    if (!rawValue) {
      setPrice('');
      return;
    }
    const numericValue = parseInt(rawValue, 10) / 100;
    setPrice(String(numericValue));
  };

  const dialogShell =
    "rounded-2xl border border-[#E5E7EB] bg-white text-[#0F172A] max-w-lg max-h-[90vh] overflow-y-auto shadow-sm dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:shadow-none";
  const labelClass = "text-[#0F172A] dark:text-[#FAFAFA]";
  const inputClass =
    "h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]";
  const selectClass =
    "w-full rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] transition-colors duration-200 focus:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:focus:border-[#60A5FA]";
  const outlineBtn =
    "rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-transparent dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={dialogShell}>
        <DialogHeader>
          <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">{t("editOpportunity.title")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className={labelClass}>{t("editOpportunity.titleRequired")}</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("editOpportunity.titlePlaceholder")}
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelClass}>{t("common.pipelineRequired")}</Label>
              <Select value={pipelineId} onValueChange={handlePipelineChange}>
                <SelectTrigger className={selectClass}>
                  <SelectValue placeholder={t("common.pick")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {pipelines.map((pipeline) => (
                      <SelectItem key={pipeline.id} value={pipeline.id}>
                        {pipeline.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className={labelClass}>{t("common.stageRequired")}</Label>
              <Select value={stageId} onValueChange={applyStage} disabled={!pipelineId}>
                <SelectTrigger className={selectClass}>
                  <SelectValue placeholder={t("common.pick")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {filteredStages.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        {stage.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>{t("editOpportunity.product")}</Label>
            <Input
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder={t("editOpportunity.productPlaceholder")}
              className={inputClass}
            />
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Valor ({symbol})</Label>
            <Input
              value={price ? formatCurrency(String(Math.round(parseFloat(price) * 100))) : ''}
              onChange={handlePriceChange}
              placeholder="0,00"
              className={inputClass}
            />
          </div>

          {derivedStatus === 'won' && (
            <>
              <div className="space-y-2">
                <Label className={labelClass}>{t("editOpportunity.saleDate")}</Label>
                <Input
                  type="date"
                  value={saleDate}
                  max={today}
                  onChange={(e) => setSaleDate(e.target.value)}
                  aria-invalid={saleDateIsFuture}
                  className={`${inputClass} [color-scheme:light] dark:[color-scheme:dark]`}
                />
                {saleDateIsFuture && (
                  <p className="text-sm text-red-500 dark:text-red-400">
                    {t("editOpportunity.saleDateFuture")}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className={labelClass}>{t("editOpportunity.seller")}</Label>
                <Select value={wonByUserId || 'none'} onValueChange={(v) => setWonByUserId(v === 'none' ? '' : v)} disabled={loadingMembers}>
                  <SelectTrigger className={selectClass}>
                    <SelectValue placeholder={loadingMembers ? 'Carregando...' : 'Selecione'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="none">{t("common.none")}</SelectItem>
                      {activeMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name || member.email}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {derivedStatus === 'lost' && (
            <>
              <div className="space-y-2">
                <Label className={labelClass}>{t("editOpportunity.lossReasonRequired")}</Label>
                <Select value={lossReasonId} onValueChange={handleReasonChange} disabled={loadingReasons}>
                  <SelectTrigger className={selectClass} aria-invalid={missingLossReason}>
                    <SelectValue placeholder={loadingReasons ? 'Carregando...' : t("loss.pickReason")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {lossReasons.map((reason) => (
                        <SelectItem key={reason.id} value={reason.id}>
                          {reason.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="__new__" className="text-blue-400">
                        <span className="flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          {t("loss.addReason")}
                        </span>
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {missingLossReason && !isAddingReason && (
                  <p className="text-sm text-red-500 dark:text-red-400">
                    {t("editOpportunity.lossReasonMissing")}
                  </p>
                )}
              </div>

              {isAddingReason && (
                <div className="space-y-2 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-3 dark:border-[#3F3F46] dark:bg-[#18181B]">
                  <Label className={labelClass}>{t("loss.newReason")}</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newReasonName}
                      onChange={(e) => setNewReasonName(e.target.value)}
                      placeholder={t("loss.newReasonPlaceholder")}
                      className={`${inputClass} flex-1`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCreateNewReason();
                        }
                      }}
                    />
                    <Button
                      onClick={handleCreateNewReason}
                      disabled={!newReasonName.trim() || isCreatingReason}
                      className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white transition-colors duration-200"
                    >
                      {isCreatingReason ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common:actions.save")}
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className={labelClass}>{t("editOpportunity.lossOwner")}</Label>
                <Select value={lostByUserId || 'none'} onValueChange={(v) => setLostByUserId(v === 'none' ? '' : v)} disabled={loadingMembers}>
                  <SelectTrigger className={selectClass}>
                    <SelectValue placeholder={loadingMembers ? 'Carregando...' : 'Selecione'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="none">{t("common.none")}</SelectItem>
                      {activeMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name || member.email}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label className={labelClass}>{t("editOpportunity.notes")}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("editOpportunity.notesPlaceholder")}
              className="min-h-[80px] resize-none rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className={outlineBtn}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSubmitting || !canSave}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white transition-colors duration-200"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common:actions.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
